import queryString from 'query-string';
import { validateQueryParameters } from '@/helpers/verifyQueryParameters';

import { createAxiosInstance } from '~/plugins/axios.plugin';

export class GradesService {
  static SERVICE_NAME = 'grades';

  constructor() {
    this.client = createAxiosInstance({
      baseURL        : process.env.GRADE_API,
      useAccessToken : true
    });
  }

  getCourseGradesContext(courseId) {
    return this.client.get(`course/${courseId}`);
  }

  createGradeCategory(courseId, { name, externalCode, sequence, isCalculated, originVariables, formula, isVisible }) {
    return this.client.post(`course/${courseId}/categories`, {
      name,
      externalCode,
      sequence,
      isCalculated,
      originVariables,
      formula,
      isVisible
    });
  }

  editGradeCategory(courseId, { id, name, sequence, isVisible }) {
    return this.client.put(`course/${courseId}/categories/${id}`, {
      name,
      sequence,
      isVisible
    });
  }

  deleteGradeCategory(courseId, { id }) {
    return this.client.delete(`course/${courseId}/categories/${id}`);
  }

  getGradeCategoryById(courseId, categoryId) {
    return this.client.get(`course/${courseId}/categories/${categoryId}`);
  }

  getGradeCategories({ courseId, variablesOrigin }) {
    const stringified = queryString.stringify({ variablesOrigin }, { skipNull: true, skipEmptyString: true });
    return this.client.get(`course/${courseId}/categories${stringified && `?${stringified}`}`);
  }

  createGradeBook(courseId, categoryId, { name, externalCode, maxValue, sequence }) {
    return this.client.post(`course/${courseId}/categories/${categoryId}/books`, {
      name,
      externalCode,
      maxValue,
      sequence
    });
  }

  editGradeBook(courseId, categoryId, { id, name, maxValue, sequence }) {
    return this.client.patch(`course/${courseId}/categories/${categoryId}/books/${id}`, {
      name,
      maxValue,
      sequence
    });
  }

  deleteGradeBook(courseId, categoryId, { id }) {
    return this.client.delete(`course/${courseId}/categories/${categoryId}/books/${id}`);
  }

  getGradeBooks({ courseId, categoryId }) {
    return this.client.get(`course/${courseId}/categories/${categoryId}/books`);
  }

  getGradeBookDetails({ courseId, categoryId, bookId, isActive }) {
    const stringified = queryString.stringify({ isActive }, { skipNull: true, skipEmptyString: true });
    return this.client.get(`course/${courseId}/categories/${categoryId}/books/${bookId}/details${stringified && `?${stringified}`}`);
  }

  getStudentsGradeBook({ courseId, categoryId, name, limit, offset }) {
    const stringified = queryString.stringify({ name, categoryId, limit, offset }, { skipNull: true, skipEmptyString: true });
    return this.client.get(`course/${courseId}/students${stringified && `?${stringified}`}`);
  }

  getStudentsGradeBookDetails({ courseId, categoryId, bookId, name, limit, offset }) {
    const stringified = queryString.stringify({ name, limit, offset }, { skipNull: true, skipEmptyString: true });
    return this.client.get(`course/${courseId}/categories/${categoryId}/books/${bookId}/students${stringified && `?${stringified}`}`);
  }

  patchGradeBook({ courseId, categoryId, bookId, body }) {
    return this.client.patch(`course/${courseId}/categories/${categoryId}/books/${bookId}`, body);
  }

  patchGradeBookDetail({ courseId, categoryId, bookId, detailId, body }) {
    return this.client.patch(`course/${courseId}/categories/${categoryId}/books/${bookId}/details/${detailId}`, body);
  }

  editGradeBookItem(courseId, categoryId, bookId, enrollmentId, { editedValue }) {
    return this.client.put(`course/${courseId}/categories/${categoryId}/books/${bookId}/items/${enrollmentId}`, { editedValue });
  }

  editGradeBookDetailItem(courseId, categoryId, bookId, detailId, enrollmentId, { editedValue }) {
    return this.client.put(`course/${courseId}/categories/${categoryId}/books/${bookId}/details/${detailId}/items/${enrollmentId}`, { editedValue });
  }

  editStudentAverge(courseId, categoryId, enrollmentId, { editedValue }) {
    return this.client.put(`course/${courseId}/categories/${categoryId}/average/${enrollmentId}`, { editedValue });
  }

  editStudentFinal(courseId, enrollmentId, { editedValue }) {
    return this.client.put(`course/${courseId}/final-grade/${enrollmentId}`, { editedValue });
  }

  getStudentGradesByCourseId(courseId) {
    return this.client.get(`me/course/${courseId}`);
  }

  submitCorrection(courseId, correctionId, data) {
    return this.client.put(`/course/${courseId}/corrections/${correctionId}`, data);
  }

  submitCorrectionWithoutCorrectionId(courseId, data) {
    return this.client.post(`/course/${courseId}/corrections`, data);
  }

  changeSequenceOfCategory(params) {
    return this.client.patch(`course/${params.courseId}/categories/${params.categoryId}/sequence`, params);
  }

  persistGradeExternalId(params) {
    return this.client.patch(`/course/${params.academicMainId}/final`, { externalId: params.externalId });
  }

  getGradeItems(params) {
    return this.client.get(`/course/${params.academicMainId}/grades`, { params: params.queryParams });
  }

  getGradeIntegrationLtiResourcers({ academicMainId, classId }) {
    return this.client.get(`/course/${academicMainId}/lti/class/${classId}/resources`);
  }

  getGradeIntegrationLtiByResource({ academicMainId, classId, resourceId }, queryParams) {
    return this.client.get(`/course/${academicMainId}/lti/class/${classId}/resources/${resourceId}/grades`, {
      params: queryParams
    });
  }

  sendGradeIntegrationLti({ academicMainId, classId, resourceId }) {
    return this.client.patch(`/course/${academicMainId}/lti/class/${classId}/resources/${resourceId}/grades`);
  }

  getGradeIntegrationLtiDetail({ academicMainId, classId, resourceId, studentId, gradeId }) {
    return this.client.get(
      `/course/${academicMainId}/lti/class/${classId}/resources/${resourceId}/students/${studentId}/grades/${gradeId}`
    );
  }

  async postH5PXAPIEvent(params) {
    await fetch(`${process.env.GRADE_API}/xApi/course/${params.academicMainId}/topic/${params.topicId}/event`, {
      method  : 'POST',
      body    : JSON.stringify(params.body),
      headers : {
        'Content-Type'    : 'application/json',
        Authorization     : localStorage.getItem(`${process.env.APPLICATION_ALIAS}_accessToken`),
        'Accept-Language' : window.$nuxt.$store.state._core.user.languageId
      },
      keepalive: true
    })
      .catch(() => null);
  }
};

export default new GradesService();
